import * as React from 'react';

import { getLocale } from '@rematter/common/locale';
import {
  enumCurrencyCode,
  enumUnit,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { useBrokeredLoadLineItemsTotals } from '@rematter/modules/brokerage/hooks/useBrokeredLoadLineItemsTotals';
import {
  convertFromPounds,
  convertToPounds,
  humanReadableShortPricingUnit,
  MeasuredValue,
  Money,
  shortenUnit,
} from '@rematter/ui-web';

import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import {
  BrokeredLoadFormLineItem,
  enumBrokeredLoadWeightType,
} from '~/modules/brokerage/types/brokeredLoad';
import { WeightUnitType } from '~/modules/trade/types/load';
import {
  DeleteButtonCell,
  FooterCell,
  HeaderCell,
  InventoryItemSelect,
  LineItemFooter,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
  PriceFieldCell,
  UnitFieldCell,
  WeightFieldCell,
} from '~/shared/components/line-items';
import { SelectPurchaseOrder } from '~/shared/components/select-fields/SelectPurchaseOrder';

const BROKERED_LOAD_REQUESTED_GRID =
  'grid-cols-[2fr_1.5fr_1fr_100px_0.8fr_var(--size-input)]';

interface BrokeredLoadRequestedTableProps {
  lineItems: BrokeredLoadFormLineItem[];
  onUpdateLineItem: (
    id: string,
    updates: Partial<BrokeredLoadFormLineItem>,
  ) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem: () => void;
  disabled?: boolean;
  purchaseOrderIds?: string[];
  weightUnit?: WeightUnitType;
  hideFooter?: boolean;
}

export const BrokeredLoadRequestedTable: React.FC<
  BrokeredLoadRequestedTableProps
> = ({
  lineItems,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
  disabled = false,
  purchaseOrderIds,
  weightUnit = 'price',
  hideFooter = false,
}) => {
  const t = useTranslations();
  const isEmpty = lineItems.length === 0;

  const { materialCount, totalNet, totalValue, currencyCode } =
    useBrokeredLoadLineItemsTotals(lineItems, weightUnit);

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={true}
      disabled={disabled}
      addButtonLabel={t.brokerage.loads.detail.materials.addMaterial}
      emptyTitle={t.brokerage.loads.detail.materials.emptyTitle}
      emptyDescription={t.brokerage.loads.detail.materials.emptyDescription}
      className="@BrokeredLoadRequestedTable"
    >
      <LineItemHeader
        gridTemplate={BROKERED_LOAD_REQUESTED_GRID}
        className="py-1"
      >
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.material}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.purchaseOrder}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.unitPrice}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.pricingUnit}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.requestedWeight}
        </HeaderCell>
        {!disabled && <HeaderCell />}
      </LineItemHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {lineItems.map((lineItem) => {
          const pricingUnitSuffix = lineItem.pricingUnit
            ? `/${shortenUnit(lineItem.pricingUnit as Unit)}`
            : undefined;

          const netWeight = lineItem.weights?.[enumBrokeredLoadWeightType.Net];
          const displayUnit =
            weightUnit === 'price'
              ? (lineItem.pricingUnit as Unit) || enumUnit.Pounds
              : enumUnit.Pounds;
          const displaySuffix = humanReadableShortPricingUnit(displayUnit);

          return (
            <LineItemRow
              key={lineItem.id}
              gridTemplate={BROKERED_LOAD_REQUESTED_GRID}
              className="w-full py-1.5"
            >
              <InventoryItemSelect
                value={lineItem.itemId}
                onChange={(itemId) =>
                  onUpdateLineItem(lineItem.id, { itemId: itemId ?? null })
                }
                disabled={disabled}
                placeholder={t.brokerage.loads.detail.materials.selectMaterial}
                className="min-w-0 overflow-hidden"
              />
              <SelectPurchaseOrder
                value={lineItem.purchaseOrderId}
                onChange={(purchaseOrderId) =>
                  onUpdateLineItem(lineItem.id, {
                    purchaseOrderId: purchaseOrderId ?? null,
                  })
                }
                ids={purchaseOrderIds}
                isDisabled={disabled}
                className="min-w-0 overflow-hidden"
              />
              <PriceFieldCell
                value={lineItem.unitPrice?.amount ?? 0}
                onChange={(amount) =>
                  onUpdateLineItem(lineItem.id, {
                    unitPrice: {
                      amount,
                      currencyCode:
                        lineItem.unitPrice?.currencyCode ??
                        enumCurrencyCode.USD,
                      __typename: 'Money',
                    },
                  })
                }
                disabled={disabled}
                suffix={pricingUnitSuffix}
              />
              <UnitFieldCell
                value={lineItem.pricingUnit || enumUnit.Pounds}
                onChange={(value) =>
                  onUpdateLineItem(lineItem.id, {
                    pricingUnit: value ?? enumUnit.Pounds,
                  })
                }
                disabled={disabled}
                formatLabel={shortenUnit}
              />
              <WeightFieldCell
                value={
                  netWeight?.value && netWeight.unit !== displayUnit
                    ? convertFromPounds(
                        convertToPounds(
                          netWeight.value,
                          netWeight.unit as Unit,
                        ),
                        displayUnit,
                      )
                    : (netWeight?.value ?? 0)
                }
                suffix={displaySuffix}
                onChange={(value) => {
                  const storedUnit =
                    (netWeight?.unit as Unit) ?? enumUnit.Pounds;
                  const storedValue =
                    storedUnit !== displayUnit
                      ? convertFromPounds(
                          convertToPounds(value, displayUnit),
                          storedUnit,
                        )
                      : value;
                  onUpdateLineItem(lineItem.id, {
                    weights: {
                      ...lineItem.weights,
                      [enumBrokeredLoadWeightType.Net]: {
                        value: storedValue,
                        unit: storedUnit,
                        __typename: 'Weight',
                      },
                    },
                  });
                }}
                disabled={disabled}
              />

              {!disabled && (
                <DeleteButtonCell
                  onDelete={() => onDeleteLineItem(lineItem.id)}
                  disabled={disabled}
                  className="size-input"
                />
              )}
            </LineItemRow>
          );
        })}
      </div>
      {!hideFooter && (
        <LineItemFooter
          className="w-full px-2 py-1 text-sm"
          gridTemplate={BROKERED_LOAD_REQUESTED_GRID}
        >
          <FooterCell className="font-semibold">
            {materialCount}{' '}
            {t.brokerage.loads.detail.footer.materialsCount(materialCount)}
          </FooterCell>
          <FooterCell />
          <FooterCell className="justify-end font-semibold">
            <Money
              amount={totalValue}
              currencyCode={currencyCode}
              locale={getLocale()}
            />
          </FooterCell>
          <FooterCell />
          <FooterCell className="justify-end font-semibold">
            <MeasuredValue {...totalNet} />
          </FooterCell>
          <FooterCell />
        </LineItemFooter>
      )}
    </LineItemTable>
  );
};
